<template>
  <PageHeader :showBreadcrumb="true" />
  <form v-if="authStore.isAdminUser && appStore.entities.length > 0" @submit.stop.prevent="onSubmit">
    <DataTable v-model:editingRows="editingRows"
               edit-mode="row"
               v-model:filters="filters"
               :value="adminConsumerComplaints"
               ref="adminConsumerComplaintsTable"
               dataKey="complaintId"
               @rowSelect="onRowSelect"
               stripedRows :loading="loading"
               paginator :rows="25"
               sortField="createdAt"
               :sortOrder="-1"
               @row-edit-save="onRowEditSave"
               filter-display="row" show-gridlines
               :pt="{
                 table: { style: 'min-width: 50rem' },
                 column: {
                   bodycell: ({ state }) => ({
                     style:  state['d_editing']&&'padding-top: 0.75rem; padding-bottom: 0.75rem'
                   })
                 }
               }">
      <template #header>
        <div class="header-components gap-3">
          <Button icon="pi pi-filter-slash" label="Clear Filters" rounded @click="clearFilter()" />
          <IconField>
            <InputText v-model="filters['global'].value" label="Keyword Search" aria-label="Keyword Search" placeholder="Keyword Search" />
            <InputIcon>
              <i class="pi pi-search" ></i>
            </InputIcon>
          </IconField>
          <Button icon="pi pi-external-link" rounded label="Export" @click="exportCSV($event)" />
        </div>
      </template>
      <template #empty> No Data. </template>
      <Column field="complaintId" header="Complaint ID" sortable frozen align-frozen="left">
        <template #filter="{ filterModel, filterCallback }">
          <InputText v-model="filterModel.value" type="text" @input="filterCallback()" placeholder="Any" style="width: 6rem" />
        </template>
        <template #body="slotProps">
          <a href="#" @click="onRowSelect(slotProps.data)">{{ slotProps.data.complaintId }}</a>
        </template>
      </Column>
      <Column field="signatureDate" header="Complaint Submission Date" sortable/>
      <Column field="healthInsuranceCompany" header="Health Insurance Company" sortable>
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect v-model="filterModel.value" @change="filterCallback()" :options="healthInsuranceCompanies" optionLabel="label" option-value="value" placeholder="Any" :maxSelectedLabels="1">
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </MultiSelect>
        </template>
        <template #body="slotProps">
          {{ CONSUMER_COMPLAINT_HEALTH_INSURANCE_COMPANIES[slotProps.data.healthInsuranceCompany] ?? slotProps.data.healthInsuranceCompany }}
        </template>
      </Column>
      <Column field="patientInsuranceId" header="Patient Insurance ID" sortable/>
      <Column field="typeOfCoverage" header="Coverage Type" sortable>
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect v-model="filterModel.value" @change="filterCallback()" :options="coverageTypes" optionLabel="label" option-value="value" placeholder="Any" :maxSelectedLabels="1">
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </MultiSelect>
        </template>
        <template #body="slotProps">
          {{ CONSUMER_COMPLAINT_TYPE_OF_COVERAGES[slotProps.data.typeOfCoverage] ?? slotProps.data.typeOfCoverage }}
        </template>
      </Column>
      <Column field="patientName" header="Patient Name" sortable>
        <template #filter="{ filterModel, filterCallback }">
          <InputText v-model="filterModel.value" type="text" @input="filterCallback()" placeholder="Any" style="width: 6rem" />
        </template>
      </Column>
      <Column field="signature" header="Name of the Person who Submitted the Complaint" sortable/>
      <Column field="relationshipToPatient" header="Relationship to Patient" sortable>
        <template #body="slotProps">
          {{ CONSUMER_COMPLAINT_PATIENT_RELATIONSHIPS[slotProps.data.relationshipToPatient] ?? slotProps.data.relationshipToPatient ?? CONSUMER_COMPLAINT_PATIENT_RELATIONSHIPS.Self }}
        </template>
      </Column>
      <Column field="documents" header="Documents" sortable>
        <template #body="slotProps">
          <div class="download-links">
            <span
              v-for="(file, index) in slotProps.data.documents"
              :key="index"
              class="file-link"
            >
              <a
                href="#"
                @click.prevent="downloadFile(file)"
                :aria-label="`Download file: ${file}`"
                :title="`Download ${file}`"
                class="download-link"
              >
                {{ file.replace("consumerComplaintSupportingDocuments_", "...") }}
                <i class="pi pi-download" aria-hidden="true" style="margin-left: 0.3em;"></i>
              </a>
              <span v-if="index < slotProps.data.documents.length - 1">,&nbsp;</span>
            </span>
          </div>
        </template>
      </Column>
      <Column field="complaintStatus" header="Status" sortable>
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect v-model="filterModel.value" @change="filterCallback()" :options="statuses" optionLabel="label" option-value="value" placeholder="Any" :maxSelectedLabels="1">
            <template #option="slotProps">
              <Tag :value="slotProps.option.label" :severity="getTagSeverity(slotProps.option.label)" />
            </template>
          </MultiSelect>
        </template>
        <template #editor="{ data, field }">
          <Select v-model="data[field]" :options="statuses" optionLabel="label" optionValue="value" placeholder="Select Status" fluid>
            <template #option="slotProps">
              <Tag :value="slotProps.option.label" :severity="getTagSeverity(slotProps.option.label)" />
            </template>
          </Select>
        </template>
        <template #body="slotProps">
          <Tag :value="CONSUMER_COMPLAINT_STATUSES[slotProps.data.complaintStatus] ?? slotProps.data.complaintStatus" :severity="getTagSeverity(slotProps.data.complaintStatus)" />
        </template>
      </Column>
      <Column field="complaintOutcome" header="Complaint Outcome" sortable>
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect v-model="filterModel.value" @change="filterCallback()" :options="outcomes" optionLabel="label" option-value="value" placeholder="Any" :maxSelectedLabels="1">
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </MultiSelect>
        </template>
        <template #editor="{ data, field }">
          <Select v-model="data[field]" :options="outcomes" optionLabel="label" optionValue="value" placeholder="Select Complaint Outcome" fluid>
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
        </template>
        <template #body="slotProps">
          {{ CONSUMER_COMPLAINT_OUTCOMES[slotProps.data.complaintOutcome] ?? slotProps.data.complaintOutcome }}
        </template>
      </Column>
      <Column field="mdhInvestigator" header="MDH Investigator" sortable>
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect v-model="filterModel.value" @change="filterCallback()" :options="staffUsers" optionLabel="label" option-value="value" placeholder="Any" :maxSelectedLabels="1">
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </MultiSelect>
        </template>
        <template #editor="{ data, field }">
          <Select v-model="data[field]" :options="staffUsers" optionLabel="label" optionValue="value" placeholder="Select MDH Investigator" fluid>
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
        </template>
        <template #body="slotProps">
          {{ CONSUMER_COMPLAINT_MDH_INVESTIGATORS[slotProps.data.mdhInvestigator] ?? slotProps.data.mdhInvestigator }}
        </template>
      </Column>
      <Column field="complaintCategory" header="Complaint Category" sortable>
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect v-model="filterModel.value" @change="filterCallback()" :options="categories" optionLabel="label" option-value="value" placeholder="Any" :maxSelectedLabels="1">
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </MultiSelect>
        </template>
        <template #editor="{ data, field }">
          <Select v-model="data[field]" :options="categories" optionLabel="label" optionValue="value" placeholder="Select Review Category" fluid>
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
        </template>
        <template #body="slotProps">
          {{ CONSUMER_COMPLAINT_CATEGORIES[slotProps.data.complaintCategory] ?? slotProps.data.complaintCategory }}
        </template>
      </Column>
      <Column field="notes" header="Notes" :exportable="false">
        <template #body="slotProps">
          <Button
            class="btn btn-success nowrap-btn"
            label="Notes"
            icon="pi pi-pen-to-square"
            @click="toggleNotesDialog(slotProps.data)"
            rounded
          />
        </template>
      </Column>
      <Column header="Actions" :rowEditor="true" style="width: 1%; min-width: 8rem" bodyStyle="text-align: center"/>
    </DataTable>
  </form>

  <Dialog
    v-if="complaintSelected"
    :visible="isNotesOpen"
    modal
    header="Consumer Complaint Notes"
    :closable="false"
    :style="{ width: '40rem' }"
    @hide="dialogStore.close()"
  >
    <Notes
      :parentType="NOTE_TYPES.Complaint"
      :parentId="complaintSelected.complaintId"
      @close="dialogStore.close()"
    />

    <template #footer>
      <div class="d-flex justify-content-end">
        <Button
          type="button"
          rounded
          raised
          label="Close"
          severity="secondary"
          @click="dialogStore.close();"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import {computed, onMounted, ref} from 'vue';
import PageHeader from "@/components/common/PageHeader.vue";
import {useAppStore} from "@/stores/appStore.js";
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from "primevue/button";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";
import MultiSelect from "primevue/multiselect";
import Select from "primevue/select";
import Tag from "primevue/tag";
import {useAuthStore} from "@/stores/authStore.js";
import {getAdminConsumerComplaints, updateAdminConsumerComplaint} from "@/services/adminService.js";
import {FilterMatchMode, FilterService} from '@primevue/core/api';
import {getPreSignedDownloadUrl} from "@/services/downloadFile.js";
import {
  getAsFormOptions,
  CONSUMER_COMPLAINT_PATIENT_RELATIONSHIPS,
  CONSUMER_COMPLAINT_HEALTH_INSURANCE_COMPANIES,
  CONSUMER_COMPLAINT_TYPE_OF_COVERAGES,
  CONSUMER_COMPLAINT_STATUSES,
  CONSUMER_COMPLAINT_OUTCOMES,
  CONSUMER_COMPLAINT_CATEGORIES,
  CONSUMER_COMPLAINT_MDH_INVESTIGATORS,
  TOAST_SAVE_LIFE,
  NOTE_TYPES
} from "@/utils/constants.js";
import {AdminConsumerComplaintModel} from "@/models/AdminConsumerComplaintModel.js";
import {useToast} from "primevue/usetoast";
import Notes from "@/components/notes/Notes.vue";
import Dialog from "primevue/dialog";
import {useDialogStore} from "@/stores/dialogStore.js";
import {AdminExternalAppealModel} from "@/models/AdminExternalAppealModel.js";
import router from "@/router/index.js";

const appStore = useAppStore();
const authStore = useAuthStore();
const toast = useToast();
const dialogStore = useDialogStore();

const isNotesOpen = computed(() => dialogStore.isOpen('consumerComplaintNotes'));

const adminConsumerComplaints = ref([]);
const adminConsumerComplaintsTable = ref();
const loading = ref(true);
const editingRows = ref([]);
const complaintSelected = ref();

// Form Select Options lists
const healthInsuranceCompanies = ref(getAsFormOptions(CONSUMER_COMPLAINT_HEALTH_INSURANCE_COMPANIES));
const coverageTypes = ref(getAsFormOptions(CONSUMER_COMPLAINT_TYPE_OF_COVERAGES));
const statuses = ref(getAsFormOptions(CONSUMER_COMPLAINT_STATUSES));
const outcomes = ref(getAsFormOptions(CONSUMER_COMPLAINT_OUTCOMES));
const categories = ref(getAsFormOptions(CONSUMER_COMPLAINT_CATEGORIES));
const staffUsers = ref(getAsFormOptions(CONSUMER_COMPLAINT_MDH_INVESTIGATORS));

const exportCSV = () => {
  adminConsumerComplaintsTable.value.exportCSV();
};

const getConsumerComplaints = async () => {
  const cc = await getAdminConsumerComplaints();
  cc.forEach((e) => {
    if (e.createdAt) {
      e.createdAt = new Date(e.createdAt).toLocaleString();
    } else {
      e.createdAt = '—';
    }
    if (!e.relationshipToPatient) {
      e.relationshipToPatient = CONSUMER_COMPLAINT_PATIENT_RELATIONSHIPS.Self;
    }
    if (e.documents) {
      e.documents = buildDownloadLinks(e.documents);
    }
  });
  adminConsumerComplaints.value = cc;
};

onMounted(() => {
  Promise.all([getConsumerComplaints()])
      .then(() => {
        loading.value = false;
      });
});

const onRowSelect = (event) => {
  const model = new AdminConsumerComplaintModel(event);
  console.log(model);
  router.push({name: 'ViewSaveAsPdfConsumerComplaint', query: {
      complaintID: model.complaintId,
    }});
};

const downloadFile = async (fileName) => {
  try {
    const responseData = await getPreSignedDownloadUrl(fileName);
    const link = document.createElement('a');
    link.href = responseData.url;
    link.download = fileName;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.click();
  } catch (error) {
    console.error('Download failed:', error);
  }
};

const buildDownloadLinks = (downloads) => {
  if (!downloads) return [];
  return downloads.split(',').map(file => file.trim());
};

const onRowEditSave = async (event) => {
  const {newData} = event;
  const model = new AdminConsumerComplaintModel(newData);

  delete model.createdAt;
  delete model.updatedAt;
  delete model.documents;

  try {
    await updateAdminConsumerComplaint(model);
    await getConsumerComplaints();
    toast.add({ group: 'top-toast', severity: 'success', summary: 'Successfully Updated Consumer Complaint', detail: `Consumer Complaint #${model.complaintId} (${model.patientName}) has been updated.`, life: TOAST_SAVE_LIFE });
  } catch (e) {
    toast.add({ group: 'top-toast', severity: 'error', summary: 'Error Updating Consumer Complaint', detail: `Consumer Complaint #${model.complaintId} (${model.patientName}) could not be updated.\n${e}`, life: TOAST_SAVE_LIFE });
  }
};

const getTagSeverity = (text) => {
  if (!text) return null;
  if (text === 'Open') return 'success';
  if (text.includes('Pending Information')) return 'warn';
  if (text === 'Closed' || text === 'Withdrawn') return 'danger';
  return null;
};

// Filters
FilterService.register('healthInsuranceCompanyFilter', (value, filterSelection) => {
  return filterOtherHandler(value, filterSelection, healthInsuranceCompanies);
});
FilterService.register('typeOfCoverageFilter', (value, filterSelection) => {
  return filterOtherHandler(value, filterSelection, coverageTypes);
});

const filterOtherHandler = (value, filterSelection = [], allOptionsRef) => {
  // No filter selected, show all
  if (value === undefined || filterSelection.length === 0) return true;

  // Extract all known option
  const allOptions = Array.isArray(allOptionsRef?.value)
      ? allOptionsRef.value.map(o => o.value ?? o.label ?? o)
      : [];

  const nonOther = allOptions.filter(v => v !== 'Other');

  const hasOther = filterSelection.includes('Other');
  const regularSelections = filterSelection.filter(v => v !== 'Other');

  // If "Other" is selected, include:
  //   - any row matching a selected regular value
  //   - any row NOT in the known list
  if (hasOther) {
    const isKnown = nonOther.includes(value);
    const matchesRegular = regularSelections.includes(value);
    return matchesRegular || !isKnown;
  }

  // Otherwise, standard inclusion logic
  return filterSelection.includes(value);
};

const filters = ref();
const initFilters = () => {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    complaintId: { value: null, matchMode: FilterMatchMode.CONTAINS },
    healthInsuranceCompany: { value: null, matchMode: 'healthInsuranceCompanyFilter' },
    typeOfCoverage: { value: null, matchMode: 'typeOfCoverageFilter' },
    patientName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    complaintStatus: { value: null, matchMode: FilterMatchMode.IN },
    complaintOutcome: { value: null, matchMode: FilterMatchMode.IN },
    mdhInvestigator: { value: null, matchMode: FilterMatchMode.IN },
    complaintCategory: { value: null, matchMode: FilterMatchMode.IN },
  };
};
initFilters();

const clearFilter = () => {
  initFilters();
};

// Open/close notes dialog
const toggleNotesDialog = async (complaint) => {
  complaintSelected.value = complaint;
  dialogStore.toggle('consumerComplaintNotes');
};
</script>

<style scoped>
.header-components {
  display: flex;
  justify-content: flex-end;
}

.download-links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.download-link {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  text-decoration: underline;
  color: var(--primary-color, #007ad9);
}

.download-link:hover {
  color: #005ea3;
}

.download-link i.pi-download {
  font-size: 0.9em;
  margin-left: 0.3em;
}

</style>